
import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Filter, MoreHorizontal, Eye, Flag, Archive, MessageSquare } from 'lucide-react';
import HomeChatbot from './HomeChatbot';
import ChatButton from '../../components/ChatButton';
import homeApi, { Metric, Action } from '../../api/homeApi';
import { useBusiness } from '../../context/BusinessContext';
import { useNavigate } from 'react-router-dom';
import { Header } from '../auth/components/Header';

const SORT_OPTIONS = [
    { label: 'Urgency', value: 'urgency' },
    { label: 'A \u2192 Z', value: 'az' },
    { label: 'Z \u2192 A', value: 'za' },
    { label: 'Date Updated', value: 'date_updated' },
];

const Home: React.FC = () => {
    const [sortOpen, setSortOpen] = useState(false);
    const [selectedSort, setSelectedSort] = useState('Urgency');
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isChatMaximized, setIsChatMaximized] = useState(false);

    const [metrics, setMetrics] = useState<Metric[]>([]);
    const [actions, setActions] = useState<Action[]>([]);
    const [loading, setLoading] = useState(true);

    // For navigation/context when clicking "View Deal"
    const { setSelected } = useBusiness();
    const navigate = useNavigate();

    useEffect(() => {
        // Always reset selection when landing on Home screen
        setSelected(null);

        const loadData = async () => {
            try {
                const [m, a] = await Promise.all([
                    homeApi.getMetrics(),
                    homeApi.getActions()
                ]);
                setMetrics(m);
                setActions(a);
            } catch (err) {
                console.error("Failed to load home data", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    const handleViewDeal = (businessName: string) => {
        // Find full business object if possible, or just mock slug
        const slug = businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        setSelected({ name: businessName, slug: slug });
        navigate('/deals');
    };

    // --- Sorting Logic ---
    const getSortedActions = () => {
        const sorted = [...actions];
        switch (selectedSort) {
            case 'A \u2192 Z':
                return sorted.sort((a, b) => a.business.localeCompare(b.business));
            case 'Z \u2192 A':
                return sorted.sort((a, b) => b.business.localeCompare(a.business));
            case 'Urgency':
                // High Risk > Medium > Low
                // Then Stage: Waiting > In Review > Approved
                const riskScore = (r: string) => (r === 'High' ? 3 : r === 'Medium' ? 2 : 1);
                return sorted.sort((a, b) => riskScore(b.risk) - riskScore(a.risk));
            case 'Date Updated':
                // We don't have explicit date in Action interface yet, so strictly this might not work perfectly 
                // without updating the API. But assuming 'notes' might indicate recentness or just stable sort for now.
                // Ideally, update Action interface to include updated_at
                return sorted;
            default:
                return sorted;
        }
    };

    const sortedActions = getSortedActions();

    return (
        <div className="flex flex-1 overflow-hidden bg-transparent">
            {/* Middle Content Area: Scrollable (Vertical + Horizontal) */}
            <div className="flex-1 overflow-auto scrollbar-hide">
                <div className="max-w-[1440px] mx-auto w-full min-w-[1200px] flex flex-col">
                    {/* Top Bar - Now aligned with content grid */}
                    <Header />

                    <div className="px-8 pb-32 pt-2 flex flex-col gap-6">
                        {/* Metrics Grid */}
                        {loading ? (
                            <div className="h-[320px] flex items-center justify-center text-gray-400">Loading Summary...</div>
                        ) : (
                            <>
                                <div className="grid grid-cols-3 gap-4">
                                    {metrics.slice(0, 3).map((m, i) => (
                                        <MetricCard key={i} {...m} />
                                    ))}
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    {metrics.slice(3, 6).map((m, i) => (
                                        <MetricCard key={i} {...m} />
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Actions Card */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-fit">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-bold text-gray-900">Actions</h2>
                                <div className="flex gap-3 relative z-[40]">
                                    {/* Sort Dropdown */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setSortOpen(!sortOpen)}
                                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 bg-white shadow-sm font-medium min-w-[140px] justify-between"
                                        >
                                            <span className="truncate">{selectedSort}</span>
                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                                        </button>

                                        {sortOpen && (
                                            <>
                                                <div className="fixed inset-0 z-0" onClick={() => setSortOpen(false)}></div>
                                                <div className="absolute right-0 mt-1 w-full min-w-[180px] bg-white border border-gray-200 rounded-md shadow-lg z-[50] py-1">
                                                    {SORT_OPTIONS.map((opt) => (
                                                        <button
                                                            key={opt.value}
                                                            onClick={() => {
                                                                setSelectedSort(opt.label);
                                                                setSortOpen(false);
                                                            }}
                                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${selectedSort === opt.label ? 'font-medium text-gray-900 bg-gray-50' : 'text-gray-600'}`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <table className="w-full text-left text-sm relative">
                                <thead className="text-gray-500 font-medium border-b border-gray-100 sticky top-0 bg-white z-10 shadow-sm">
                                    <tr>
                                        <th className="pb-4 pt-2 pl-2 font-semibold w-[25%]">Business</th>
                                        <th className="pb-4 pt-2 font-semibold w-[15%]">Amount</th>
                                        <th className="pb-4 pt-2 font-semibold w-[15%]">Stage</th>
                                        <th className="pb-4 pt-2 font-semibold w-[15%]">Risk</th>
                                        <th className="pb-4 pt-2 font-semibold w-[25%]">Notes</th>
                                        <th className="pb-4 pt-2 w-[5%]"></th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-700">
                                    {loading ? (
                                        <tr><td colSpan={6} className="py-8 text-center text-gray-400">Loading Actions...</td></tr>
                                    ) : sortedActions.map((action, i) => (
                                        <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors group">
                                            <td className="py-4 pl-2 font-semibold text-gray-900 align-top">
                                                {action.business}
                                                <div className="text-xs text-gray-400 font-normal mt-0.5">(the_{action.business.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')})</div>
                                            </td>
                                            <td className="py-4 font-bold text-gray-900 align-top">{action.amount}</td>
                                            <td className="py-4 align-top">
                                                <span className={`px-2.5 py-1 rounded text-xs font-semibold whitespace-nowrap ${action.stage === 'In Review' ? 'bg-purple-100 text-purple-700' :
                                                    action.stage === 'Waiting' ? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-700'
                                                    }`}>{action.stage}</span>
                                            </td>
                                            <td className="py-4 align-top">
                                                <span className={`px-2.5 py-1 rounded text-xs font-semibold whitespace-nowrap ${action.risk === 'High' ? 'bg-red-100 text-red-700' :
                                                    action.risk === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-green-100 text-green-700'
                                                    }`}>{action.risk}</span>
                                            </td>
                                            <td className="py-4 text-gray-500 max-w-[200px] truncate align-top">{action.notes}</td>
                                            <td className="py-4 text-right align-top">
                                                <ActionsMenu onOpen={() => handleViewDeal(action.business)} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-6">
                                <div className="text-sm text-gray-500">
                                    Showing <span className="font-medium">1</span> to <span className="font-medium">{sortedActions.length}</span> of <span className="font-medium">{sortedActions.length}</span> results
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        disabled
                                        className="px-4 py-2 text-sm font-medium text-gray-400 bg-gray-50 rounded-md cursor-not-allowed border border-gray-200"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => alert("Pagination functionality coming soon!")}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 shadow-sm transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Flex sibling Chatbot (No fixed/transform logic) */}
            {isChatOpen && (
                <div className="h-full border-l border-gray-200 transition-all duration-300">
                    <HomeChatbot
                        isMaximized={isChatMaximized}
                        onToggleMaximize={() => setIsChatMaximized(!isChatMaximized)}
                        onClose={() => setIsChatOpen(false)}
                    />
                </div>
            )}

            {!isChatOpen && (
                <ChatButton handleShowChat={() => setIsChatOpen(true)} />
            )}
        </div>
    );
};

const ActionsMenu: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block text-left" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
                <MoreHorizontal className="w-5 h-5" />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-100 z-50 py-1">
                    <button
                        onClick={() => {
                            onOpen();
                            setIsOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 text-left"
                    >
                        <Eye className="w-4 h-4 text-gray-500" />
                        View Deal
                    </button>
                    <button
                        onClick={() => {
                            alert("Risk flag has been added to this deal.");
                            setIsOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 text-left"
                    >
                        <Flag className="w-4 h-4 text-gray-500" />
                        Flag Risk
                    </button>
                    <button
                        onClick={() => {
                            alert("Deal archived.");
                            setIsOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 text-left"
                    >
                        <Archive className="w-4 h-4" />
                        Archive
                    </button>
                </div>
            )}
        </div>
    );
};

const MetricCard: React.FC<{ label: string; value: string; change: string; changeType: string }> = ({ label, value, change, changeType }) => {
    let changeColor = 'bg-gray-100 text-gray-600';
    if (changeType === 'positive') changeColor = 'bg-green-100 text-green-700';
    if (changeType === 'negative') changeColor = 'bg-yellow-100 text-yellow-700';
    if (changeType === 'neutral-purple') changeColor = 'bg-purple-100 text-purple-700';

    return (
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between h-[150px] hover:shadow-md transition-shadow">
            <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-1.5">{label}</h3>
                <div className="text-3xl font-bold text-gray-900 tracking-tight">{value}</div>
            </div>
            <div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${changeColor}`}>
                    {change}
                </span>
            </div>
        </div>
    );
}

export default Home;
